// @flow
import React from 'react'
import classNames from 'classnames'
import I18n from 'i18n-react'
import striptags from 'striptags'
import Immutable from 'seamless-immutable'
import Modal from 'reactstrap/lib/Modal'
import ModalBody from 'reactstrap/lib/ModalBody'
import ModalHeader from 'reactstrap/lib/ModalHeader'
import { pathOr, assocPath, filter, pipe } from 'ramda'

import LoadableHtmlRender from './LoadableHtmlRender'

// components
import { FieldForm, TextForm } from '../../Components/'
import VehicleWindscreenApp from '../../Components/_miniApps/VehicleWindscreenApp'

// config
import { config } from '../../Config/ConfigLoader'

import { getValueForPath } from '../FilterFunctions'

// types
import type { ExpandedSpecificationDTO } from '../Types/Field'
import type { ProductContent } from '../Types/ProductContent'
import type { InputHelpsType } from '../Types/InputHelpType'
import type { ValidationErrorType } from '../Types/ValidationError'
import type { ValueObject } from '../Types/Values'
import type { ResourcesType } from '../Types/Resources'
import type { OneQuoteDocuments } from '../Types/DocumentTypes'
import type { TariffContentType } from '../Types/TariffsContentType'
import type { ActiveSettingsType, IdentifyObject } from '../Types/SettingsTypes'
import type { AsMutable } from '../Types/ImmutableTypes'

type SupplementaryInsuranceItemProps = {
  activeSettings: ActiveSettingsType & AsMutable<ActiveSettingsType>,
  allowedTags?: Array<string>,
  content: ProductContent,
  debug?: boolean,
  documents: OneQuoteDocuments,
  hideDisabled?: boolean,
  icon: string,
  id: string,
  identifyObject?: IdentifyObject,
  inputHelps?: InputHelpsType,
  isBackOffice?: boolean,
  narrow?: boolean,
  onChangeSupplementaryInsurances: (data: Object, paths: Array<string>) => void,
  onChangeUserFormData: (data: Object, paths: Array<string>) => void,
  onChangeValue: (key: string, value: string | Object, pathArray: Array<string>) => void,
  onRefreshCoveragePrices: (data: any) => void,
  onRemoveClick: (id: string) => void,
  parent?: boolean,
  pushIsPristine: boolean,
  resources?: ResourcesType,
  specification?: ExpandedSpecificationDTO,
  supplementaryInsurances: { [key: string]: any },
  tariffContent: TariffContentType,
  templateVariables?: ValueObject,
  title: string,
  token: string,
  validationErrors: ValidationErrorType,
  valueNames: { [key: string]: { [key: string]: string } },
}

type SupplementaryInsuranceItemState = {
  modalOpened: boolean,
  invalidFields: Array<string>,
}

export default class SupplementaryInsuranceItem extends React.Component<
  SupplementaryInsuranceItemProps,
  SupplementaryInsuranceItemState,
> {
  static defaultProps = {
    chosenValue: '',
    hideDisabled: false,
    narrow: false,
    parent: false,
  }

  state = {
    modalOpened: false,
    invalidFields: [],
  }

  getChildrenNames = () => {
    const { parent, specification, id } = this.props
    if (parent) {
      return specification.types
        .filter((type) => type.name === id)
        .reduce((acc, parentType) => parentType.fields.map((field) => field.name), [])
    }
    return []
  }

  handleOnRemoveClick = (sendRefresh: boolean = true) => {
    const {
      id,
      onRemoveClick,
      parent,
      supplementaryInsurances,
      onChangeSupplementaryInsurances,
      onRefreshCoveragePrices,
    } = this.props
    if (parent) {
      let data = supplementaryInsurances
      if (data.asMutable) {
        data = data.asMutable()
      }
      let key = ''
      this.getChildrenNames().map((childrenKey) => {
        key = childrenKey
        delete data[childrenKey]
      })
      onChangeSupplementaryInsurances(data, [key])
      if (sendRefresh) {
        onRefreshCoveragePrices(data)
      }
    } else {
      onRemoveClick(id)
    }
  }

  handleOpenModal = () => {
    this.setState({ modalOpened: true })
  }

  handleCloseModal = (dataPush: any = false, changedKeys?: Array<string>) => {
    let supplementaryInsurances = { ...this.props.supplementaryInsurances }
    let newData = dataPush || supplementaryInsurances
    let keys = changedKeys ? changedKeys.filter((key) => key !== 'seats') : []
    let newState = {
      invalidFields: [],
      modalOpened: true,
    }
    if (this.props.parent && this.getChildrenNames().length) {
      this.getChildrenNames().map((childrenName) => {
        if (newData[childrenName]) {
          keys = [childrenName]
        }
      })
    }
    if (
      this.props.parent &&
      keys.length &&
      this.props.content.components[this.props.id] &&
      this.props.content.components[this.props.id].requiredFields
    ) {
      if (
        (changedKeys && changedKeys.includes('seats')) ||
        this.props.content.components[this.props.id].requiredFields.includes('seats')
      ) {
        newData = this.getChildrenNames().reduce(
          (acc, key) => {
            if (typeof acc[key] === 'object' && acc[key]) {
              acc[key] = {
                ...acc[key],
                seats: newData.seats,
              }
            }
            return acc
          },
          { ...newData },
        )
      }
      const invalidFields = this.props.content.components[this.props.id].requiredFields.reduce((acc, fieldName) => {
        if (!newData[keys[0]]) {
          return acc
        }
        const requiredField = newData[keys[0]][fieldName]
        if (
          !(
            (typeof requiredField === 'string' && requiredField.length > 0) ||
            typeof requiredField === 'boolean' ||
            (typeof requiredField === 'number' && requiredField > 0)
          )
        ) {
          acc.push(fieldName)
        }
        return acc
      }, [])
      if (invalidFields.length === 0) {
        newState.modalOpened = false
        this.props.onRefreshCoveragePrices(newData)
        this.handleChangeSupplementaryInsurances(newData, changedKeys || [])
      } else {
        if (Array.isArray(changedKeys) && changedKeys.length > 0) {
          this.props.onChangeValue(changedKeys[0], '', changedKeys)
        }
        newState.invalidFields = invalidFields
      }
    } else {
      newState.modalOpened = false
      this.props.onRefreshCoveragePrices(newData)
    }
    this.setState(newState)
  }

  handleOnChange = (key: string, value: string | Object, pathArray: Array<string>, closeOnSelect: boolean = true) => {
    const { parent, onChangeValue, supplementaryInsurances, onChangeSupplementaryInsurances } = this.props

    if (!parent) {
      onChangeValue(key, value, pathArray)
      let data = supplementaryInsurances.asMutable ? supplementaryInsurances : new Immutable({})
      data = data.setIn(pathArray, value)
      if (
        (typeof closeOnSelect === 'boolean' && closeOnSelect) ||
        (typeof closeOnSelect === 'string' && pathArray.includes(closeOnSelect))
      ) {
        this.handleCloseModal(data)
      }
    } else {
      pathArray = pathArray.slice(1)
      const changed = this.getChildrenNames().reduce((acc, childrenName) => {
        if (pathArray.includes(childrenName)) {
          // $FlowIssue
          acc[childrenName] = {
            [pathArray[1]]: value,
          }
          if (supplementaryInsurances.seats) {
            acc[childrenName].seats = supplementaryInsurances.seats
          }
        }
        return acc
      }, {})

      const changedKeys = Object.keys(changed)
      if (changedKeys.length) {
        let data = supplementaryInsurances.asMutable
          ? supplementaryInsurances.merge(changed, { deep: true })
          : new Immutable(changed)
        onChangeSupplementaryInsurances(data, changedKeys)
        if (
          (typeof closeOnSelect === 'boolean' && closeOnSelect) ||
          (typeof closeOnSelect === 'string' && key === closeOnSelect)
        ) {
          this.handleCloseModal(data, changedKeys)
        }
      }
    }
  }

  handleChangeSupplementaryInsurances = (
    formData: Object,
    paths: Array<string>,
    closeOnSelect: boolean | string = false,
  ) => {
    if (paths.includes('seats') && !!formData.seats && this.state.invalidFields.includes('seats')) {
      const newInvalidFields = this.state.invalidFields.filter((v) => v !== 'seats')
      this.setState({ invalidFields: newInvalidFields })
    }
    if (
      (typeof closeOnSelect === 'boolean' && closeOnSelect) ||
      (typeof closeOnSelect === 'string' && paths.includes(closeOnSelect))
    ) {
      this.handleCloseModal(formData, paths)
    }
    this.props.onChangeSupplementaryInsurances(formData, paths)
  }

  handleSelectLimit = (value: string) => {
    if (this.props.resources) {
      const resources = getValueForPath('coverages.glass.default.limits', 0, this.props.resources)
      let limit = resources.find((oneLimit) => oneLimit > value) || resources[resources.length - 1]
      this.handleOnChange('glass', limit, ['glass', 'glass', 'limit'], true)
    }
  }

  render() {
    const {
      activeSettings,
      debug,
      documents,
      hideDisabled,
      icon,
      id,
      identifyObject,
      inputHelps,
      isBackOffice,
      narrow,
      onQuoteValidate,
      parent,
      pushIsPristine,
      resources,
      specification,
      supplementaryInsurances,
      tariffContent,
      templateVariables,
      title,
      token,
      validationErrors,
      valueNames,
    } = this.props
    let { content } = this.props

    const classes = ['card pricing-table border-active-object', { 'pricing-table-recommended': this.state.modalOpened }]

    if (narrow) {
      classes.push('pricing-table-narrow')
    }

    let disabled = !(resources && resources.coverages && resources.coverages[id])
    if (!disabled) {
      disabled =
        specification.fields.filter((field) => field.name === id && !field.enabled).length > 0 ? true : disabled
    }
    const buttonClasses = ['btn btn-secondary text-nowrap', { disabled: disabled }]

    if (hideDisabled && disabled) {
      return null
    }

    let included = !disabled
    if (resources && resources.coverages && resources.coverages[id]) {
      included = !!resources.coverages[id].included
    }

    let chosenValue = supplementaryInsurances[id]
    this.getChildrenNames().map((childrenKey) => {
      if (supplementaryInsurances[childrenKey]) {
        chosenValue = supplementaryInsurances[childrenKey]
      }
    })

    // $FlowIssue
    const coverageContent: { [string]: string } =
      resources && resources.coverages && resources.coverages[id] ? resources.coverages[id] : false
    const allowedTags = this.props.allowedTags || config.getConfig('allowedHtmlTags.SupplementaryInsuranceItem')

    if (!coverageContent) {
      return null
    }

    let closeOnSelect = true
    if (!coverageContent.closeOnSelect && typeof coverageContent.closeOnSelect === 'boolean') {
      closeOnSelect = false
    }
    if (coverageContent.closeOnSelect && typeof coverageContent.closeOnSelect === 'string') {
      closeOnSelect = coverageContent.closeOnSelect
    }

    let closeOnSelectCombination = false
    if (coverageContent.closeOnSelect && typeof coverageContent.closeOnSelect === 'boolean') {
      closeOnSelectCombination = true
    }
    if (coverageContent.closeOnSelect && typeof coverageContent.closeOnSelect === 'string') {
      closeOnSelectCombination = coverageContent.closeOnSelect
    }

    const data = parent ? Immutable({ [id]: supplementaryInsurances }) : supplementaryInsurances

    // filter child without content in resources
    if (parent) {
      content = pipe(
        assocPath(
          ['components', id, 'children'],
          filter(
            (item) => !!pathOr(false, ['coverages', item], resources),
            pathOr([], ['components', id, 'children'], content),
          ),
        ),
        assocPath(
          ['types', id, 'fieldOrder'],
          filter(
            (item) => !!pathOr(false, ['coverages', item], resources),
            pathOr([], ['types', id, 'fieldOrder'], content),
          ),
        ),
      )(content)
    }

    return (
      <div className={classNames(classes)}>
        {!!chosenValue && [
          <button className="edit-card" key="quote-edit" name="button" onClick={this.handleOpenModal} type="button">
            <i className="pfp pfp-edit" />
          </button>,
          <button
            className="close-card"
            key="quote-close"
            name="button"
            onClick={this.handleOnRemoveClick}
            type="button"
          >
            <i className="pfp pfp-close" />
          </button>,
        ]}
        {icon && <span className={icon} />}
        <strong className="title w-100">{title}</strong>
        {included && <small className="cover-parent">{I18n.translate('general.insuranceIncluded')}</small>}
        {chosenValue && (
          <div>
            <strong>{I18n.translate('general.chosen')}</strong>
            <div className="chosen-values text-wrap">
              <TextForm
                blockClassName="chosen-values"
                className="form-row justify-content-center"
                content={content}
                data={data}
                debug={debug}
                renderWithoutBlockWrappers
                resources={resources}
                specification={specification}
                tabKey={id}
                tabs
              />
            </div>
          </div>
        )}
        {!included && !disabled && !chosenValue && (
          <button className={classNames(buttonClasses)} onClick={this.handleOpenModal} type="button">
            {I18n.translate('general.arrangeSupplementaryInsurance')}
          </button>
        )}
        {!included && disabled && (
          <button className={classNames(buttonClasses)} type="button" disabled>
            {I18n.translate('general.disabledSupplementaryInsurance')}
          </button>
        )}
        <Modal
          className="modal-dialog modal-lg h-100 h-sm-auto mx-0 my-0 mx-sm-auto my-sm-2"
          contentClassName="modal-content h-100 h-sm-auto border-0 border-sm-primary"
          isOpen={this.state.modalOpened}
          toggle={() => {
            this.handleCloseModal()
          }}
        >
          <ModalHeader>
            <button
              className="close"
              onClick={() => {
                this.handleCloseModal()
              }}
              type="button"
            >
              <i className="pfp pfp-close" />
            </button>
          </ModalHeader>
          <ModalBody className="modal-body d-flex d-sm-block align-items-center">
            <div className="p-2 mw-100">
              <strong className="d-block">{I18n.translate('components.supplementaryInsurance.titles.info')}</strong>
              <span>
                <LoadableHtmlRender label={striptags(coverageContent.general, allowedTags)} />
              </span>
              <div className="py-4">
                {!!coverageContent.participation && (
                  <p>
                    <LoadableHtmlRender label={striptags(coverageContent.participation, allowedTags)} />
                  </p>
                )}
                {!!coverageContent.photo && (
                  <p>
                    <LoadableHtmlRender label={striptags(coverageContent.photo, allowedTags)} />
                  </p>
                )}
              </div>
              {id === 'glass' && (
                <VehicleWindscreenApp
                  activeSettings={activeSettings}
                  content={content}
                  data={supplementaryInsurances}
                  formData={supplementaryInsurances}
                  isBackOffice={isBackOffice}
                  miniAppData={{
                    identifyObject,
                    onChangeUserFormData: this.props.onChangeUserFormData,
                  }}
                  onChangeFormData={this.handleSelectLimit}
                  prefix=""
                  token={token}
                />
              )}
              {this.state.modalOpened && (
                <FieldForm
                  blockClassName=""
                  blockClassNameForR=""
                  blockContentClassName=""
                  content={content}
                  data={data}
                  debug={debug}
                  formData={supplementaryInsurances}
                  inputHelps={inputHelps}
                  miniAppData={{
                    documents,
                    tariffContent,
                    invalidFields: this.state.invalidFields,
                    identifyObject,
                    onQuoteValidate,
                  }}
                  miniApps={{
                    'injuryPassenger-seats': 'seatsMiniApp',
                    [id]: 'pdfMiniApp',
                  }}
                  onChangeFormData={(formData: Object, paths: Array<string>, closeOnSelectForce = false) => {
                    this.handleChangeSupplementaryInsurances(
                      formData,
                      paths,
                      closeOnSelectForce || closeOnSelectCombination,
                    )
                  }}
                  onChangeValue={(key: string, value: string | Object, pathArray: Array<string>) => {
                    this.handleOnChange(key, value, pathArray, closeOnSelect)
                  }}
                  pushIsPristine={pushIsPristine}
                  resources={resources}
                  showBlockTitle={false}
                  specification={specification}
                  templateVariables={templateVariables}
                  title=""
                  validationErrors={validationErrors}
                  valueNames={valueNames}
                />
              )}
            </div>
          </ModalBody>
        </Modal>
      </div>
    )
  }
}
